import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Filter, Grid, List, ChevronDown, ArrowUpDown,
  SlidersHorizontal, X, Search, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { CategorySidebar, SubcategoryPills } from '../components/CategoryComponents';
import { CurrencyDisplay } from '../components/CurrencyDisplay';
import { useLanguage } from '../contexts/LanguageContext';
import { useQuote } from '../contexts/QuoteContext';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { getFirstImageUrl } from '../utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Product Card Component
 */
const ProductCard = ({ product, viewMode = 'grid' }) => {
  const { language } = useLanguage();
  const { addToCart, isInCart, addToQuote, isInQuote } = useQuote();
  const isEn = language === 'en';
  
  const name = isEn ? product.name_en : (product.name_pt || product.name_en);
  const imageUrl = getFirstImageUrl(product.images, true);
  const canBuyNow = !product.is_quote_only;
  const inCart = isInCart(product.product_id);
  const inQuote = isInQuote(product.product_id);
  
  const handleAction = () => {
    if (canBuyNow) {
      if (!inCart) addToCart(product, 1);
    } else {
      if (!inQuote) addToQuote(product, 1);
    }
  };
  
  const isAdded = canBuyNow ? inCart : inQuote;
  const btnLabel = isAdded
    ? (isEn ? 'Added' : 'Adicionado')
    : canBuyNow
      ? (isEn ? 'Add to Cart' : 'Adicionar')
      : (isEn ? 'Request Quote' : 'Pedir Cotacao');
  
  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4 hover:shadow-md transition-shadow" data-testid={`product-card-${product.product_id}`}>
        <Link to={`/products/${product.slug || product.product_id}`} className="flex-shrink-0">
          <div className="w-32 h-32 rounded-lg overflow-hidden bg-slate-100">
            {imageUrl ? (
              <img src={imageUrl} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">No image</div>
            )}
          </div>
        </Link>
        
        <div className="flex-1 min-w-0">
          <Link to={`/products/${product.slug || product.product_id}`}>
            <h3 className="font-semibold text-slate-800 hover:text-[#1E3A8A] truncate">{name}</h3>
          </Link>
          <p className="text-sm text-slate-500 mt-1">{product.supplier_name}</p>
          <div className="mt-2"><CurrencyDisplay amountZAR={product.price_zar} showBoth /></div>
          <Button
            size="sm"
            className={`mt-3 ${isAdded ? 'bg-green-600' : canBuyNow ? 'bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A]' : 'bg-[#1E3A8A] hover:bg-[#1E3A8A]/90'}`}
            onClick={handleAction}
            disabled={isAdded}
          >
            {btnLabel}
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`product-card-${product.product_id}`}>
      <Link to={`/products/${product.slug || product.product_id}`}>
        <div className="aspect-square overflow-hidden bg-slate-100">
          {imageUrl ? (
            <img src={imageUrl} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">No image</div>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        <Link to={`/products/${product.slug || product.product_id}`}>
          <h3 className="font-medium text-slate-800 hover:text-[#1E3A8A] line-clamp-2 h-12">{name}</h3>
        </Link>
        <p className="text-xs text-slate-500 mt-1 truncate">{product.supplier_name}</p>
        <div className="mt-3"><CurrencyDisplay amountZAR={product.price_zar} showBoth /></div>
        <Button
          size="sm"
          className={`w-full mt-3 ${isAdded ? 'bg-green-600' : canBuyNow ? 'bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] font-bold' : 'bg-[#1E3A8A] hover:bg-[#1E3A8A]/90'}`}
          onClick={handleAction}
          disabled={isAdded}
        >
          {btnLabel}
        </Button>
      </div>
    </div>
  );
};

/**
 * Empty State with Suggestions Component
 * Shows popular products when no results found
 */
const EmptyStateWithSuggestions = ({ categoryName, onViewAll }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  const [popularProducts, setPopularProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const fetchPopular = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products?limit=8`);
      setPopularProducts(response.data.products || []);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPopular();
  }, [fetchPopular]);
  
  return (
    <div className="py-8">
      {/* No Results Message */}
      <div className="text-center mb-10">
        <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
          <Search className="w-10 h-10 text-slate-300" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">
          {categoryName 
            ? (isEn ? `No products in "${categoryName}" yet` : `Ainda sem produtos em "${categoryName}"`)
            : (isEn ? 'No products found' : 'Nenhum produto encontrado')
          }
        </h3>
        <p className="text-slate-500 mb-4">
          {isEn 
            ? "We're adding new products daily. Check out these popular items:"
            : "Estamos adicionando novos produtos diariamente. Confira estes itens populares:"
          }
        </p>
        <Button 
          variant="outline" 
          onClick={onViewAll}
          className="border-[#1E3A8A] text-[#1E3A8A]"
        >
          {isEn ? 'View All Products' : 'Ver Todos os Produtos'}
        </Button>
      </div>
      
      {/* Popular Products */}
      {!loading && popularProducts.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#F59E0B]" />
            {isEn ? 'Popular Products' : 'Produtos Populares'}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {popularProducts.slice(0, 4).map(product => (
              <ProductCard key={product.product_id} product={product} viewMode="grid" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Products Page with Category Navigation (Takealot Style)
 */
const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const isEn = language === 'en';
  
  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters from URL
  const categorySlug = searchParams.get('category');
  const sortBy = searchParams.get('sort') || 'created_at';
  const sortOrder = searchParams.get('order') || 'desc';
  const minPrice = searchParams.get('min_price');
  const maxPrice = searchParams.get('max_price');
  const page = parseInt(searchParams.get('page') || '1');
  const limit = 20;
  
  // Price range state
  const [priceRange, setPriceRange] = useState([0, 100000]);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories/tree`);
      setCategories(response.data);
    } catch (err) {
      console.error('Category fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/products?skip=${(page - 1) * limit}&limit=${limit}`;
        
        if (categorySlug) {
          // Find category by slug
          const findCategory = (cats, slug) => {
            for (const cat of cats) {
              if (cat.slug === slug) return cat;
              if (cat.children) {
                const found = findCategory(cat.children, slug);
                if (found) return found;
              }
            }
            return null;
          };
          const category = findCategory(categories, categorySlug);
          if (category) {
            url += `&category_id=${category.category_id}`;
          }
        }
        
        if (minPrice) url += `&min_price=${minPrice}`;
        if (maxPrice) url += `&max_price=${maxPrice}`;
        
        const response = await axios.get(url);
        setProducts(response.data.products || []);
        setTotal(response.data.total || 0);
      } catch {
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    
    if (categories.length > 0 || !categorySlug) {
      fetchProducts();
    }
  }, [categorySlug, page, minPrice, maxPrice, categories]);
  
  // Get current category info
  const currentCategory = useMemo(() => {
    if (!categorySlug) return null;
    
    const findCategory = (cats, slug) => {
      for (const cat of cats) {
        if (cat.slug === slug) return cat;
        if (cat.children) {
          const found = findCategory(cat.children, slug);
          if (found) return found;
        }
      }
      return null;
    };
    return findCategory(categories, categorySlug);
  }, [categorySlug, categories]);
  
  // Get subcategories for current category
  const subcategories = useMemo(() => {
    if (!currentCategory) return [];
    return currentCategory.children || [];
  }, [currentCategory]);
  
  // Handle category change
  const handleCategoryChange = (slug) => {
    const newParams = new URLSearchParams(searchParams);
    if (slug) {
      newParams.set('category', slug);
    } else {
      newParams.delete('category');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };
  
  // Handle price filter
  const applyPriceFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    if (priceRange[0] > 0) {
      newParams.set('min_price', priceRange[0].toString());
    } else {
      newParams.delete('min_price');
    }
    if (priceRange[1] < 100000) {
      newParams.set('max_price', priceRange[1].toString());
    } else {
      newParams.delete('max_price');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };
  
  // Handle sort change
  const handleSortChange = (value) => {
    const [sort, order] = value.split('_');
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', sort);
    newParams.set('order', order);
    setSearchParams(newParams);
  };
  
  // Pagination
  const totalPages = Math.ceil(total / limit);
  
  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
    window.scrollTo(0, 0);
  };
  
  return (
    <Layout>
      <SEOHead
        title="Products — Buy from South Africa | The Guy in SA"
        description="Browse our catalog of products sourced from South Africa. Electronics, vehicles, industrial equipment and more. Delivered to Mozambique."
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://theguyinsa.co.za"},
            {"@type": "ListItem", "position": 2, "name": "Products", "item": "https://theguyinsa.co.za/products"},
          ],
        }}
      />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-[#1E3A8A]">{isEn ? 'Home' : 'Início'}</Link>
          <span>/</span>
          <span className="text-slate-800 font-medium">
            {currentCategory 
              ? (isEn ? currentCategory.name_en : currentCategory.name_pt) 
              : (isEn ? 'All Products' : 'Todos os Produtos')
            }
          </span>
        </nav>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800">
              {currentCategory 
                ? (isEn ? currentCategory.name_en : currentCategory.name_pt) 
                : (isEn ? 'All Products' : 'Todos os Produtos')
              }
            </h1>
            <p className="text-slate-500 mt-1">
              {total} {isEn ? 'products found' : 'produtos encontrados'}
            </p>
          </div>
          
          {/* View & Sort Controls */}
          <div className="flex items-center gap-3">
            {/* Mobile Filter Toggle */}
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              {isEn ? 'Filters' : 'Filtros'}
            </Button>
            
            {/* Sort Dropdown */}
            <select
              value={`${sortBy}_${sortOrder}`}
              onChange={(e) => handleSortChange(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            >
              <option value="created_at_desc">{isEn ? 'Newest' : 'Mais Recente'}</option>
              <option value="price_zar_asc">{isEn ? 'Price: Low to High' : 'Preço: Menor ao Maior'}</option>
              <option value="price_zar_desc">{isEn ? 'Price: High to Low' : 'Preço: Maior ao Menor'}</option>
              <option value="name_en_asc">{isEn ? 'Name: A-Z' : 'Nome: A-Z'}</option>
            </select>
            
            {/* View Mode */}
            <div className="hidden sm:flex items-center border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-[#1E3A8A] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-[#1E3A8A] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Subcategory Pills (Mobile & Desktop) */}
        {subcategories.length > 0 && (
          <div className="mb-6">
            <SubcategoryPills 
              subcategories={subcategories} 
              activeSlug={categorySlug}
              onSelect={handleCategoryChange}
            />
          </div>
        )}
        
        <div className="flex gap-8">
          {/* Sidebar - Desktop */}
          <aside className={`
            ${showFilters ? 'fixed inset-0 z-50 bg-white p-4 overflow-auto lg:relative lg:inset-auto lg:z-auto lg:bg-transparent lg:p-0' : 'hidden'}
            lg:block w-full lg:w-64 flex-shrink-0
          `}>
            {/* Close button for mobile */}
            <div className="flex items-center justify-between mb-4 lg:hidden">
              <h2 className="font-semibold">{isEn ? 'Filters' : 'Filtros'}</h2>
              <button onClick={() => setShowFilters(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Categories */}
            <CategorySidebar 
              categories={categories.flatMap(c => [c, ...(c.children || [])])}
              activeSlug={categorySlug}
              onCategorySelect={(slug) => {
                handleCategoryChange(slug);
                setShowFilters(false);
              }}
            />
            
            {/* Price Filter */}
            <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-4">
                {isEn ? 'Price Range' : 'Faixa de Preço'}
              </h3>
              
              <div className="px-2">
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={100000}
                  step={1000}
                  className="mb-4"
                />
              </div>
              
              <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
                <span>R {priceRange[0].toLocaleString()}</span>
                <span>R {priceRange[1].toLocaleString()}</span>
              </div>
              
              <Button 
                size="sm" 
                className="w-full bg-[#1E3A8A]"
                onClick={applyPriceFilter}
              >
                {isEn ? 'Apply' : 'Aplicar'}
              </Button>
            </div>
          </aside>
          
          {/* Products Grid */}
          <main className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
                    <div className="aspect-square bg-slate-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                      <div className="h-6 bg-slate-200 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <EmptyStateWithSuggestions 
                categoryName={currentCategory ? (isEn ? currentCategory.name_en : currentCategory.name_pt) : null}
                onViewAll={() => handleCategoryChange(null)}
              />
            ) : (
              <>
                <div className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
                    : 'space-y-4'
                }>
                  {products.map(product => (
                    <ProductCard 
                      key={product.product_id} 
                      product={product} 
                      viewMode={viewMode}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      {isEn ? 'Previous' : 'Anterior'}
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`
                              w-8 h-8 rounded-lg text-sm font-medium transition-colors
                              ${page === pageNum 
                                ? 'bg-[#1E3A8A] text-white' 
                                : 'text-slate-600 hover:bg-slate-100'
                              }
                            `}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    >
                      {isEn ? 'Next' : 'Próximo'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default ProductsPage;
